/* eslint-disable */
import { MEMORY_CONFIG } from '@/constants/memoryConfig'

export class HistoricalStatsService {
  static archiveThresholdDays = 90 // Archive races older than 90 days

  static createChocoboCareerSummary(chocobo, currentDay) {
    if (!chocobo.raceHistory || chocobo.raceHistory.length === 0) {
      return this.createEmptyCareerSummary(chocobo)
    }

    const summary = {
      chocoboId: chocobo.id,
      name: chocobo.name,
      color: chocobo.color,
      totalRaces: chocobo.raceHistory.length,
      careerWins: 0,
      careerEarnings: 0,
      bestTimes: {},
      circuitStats: {},
      monthlyStats: {},
      recentForm: this.calculateRecentForm(chocobo.raceHistory),
      careerSpan: {
        firstRace: null,
        lastRace: null,
        activeDays: 0
      }
    }

    // Process each race
    chocobo.raceHistory.forEach(race => {
      this.updateCareerStats(summary, race, currentDay)
    })

    // Calculate derived stats
    summary.winPercentage = (summary.careerWins / summary.totalRaces * 100).toFixed(1)
    summary.averagePosition = this.calculateAveragePosition(chocobo.raceHistory)
    summary.peakPerformancePeriod = this.findPeakPerformance(chocobo.raceHistory)

    return summary
  }

  static createEmptyCareerSummary(chocobo) {
    return {
      chocoboId: chocobo.id,
      name: chocobo.name,
      color: chocobo.color,
      totalRaces: 0,
      careerWins: 0,
      careerEarnings: 0,
      bestTimes: {},
      circuitStats: {},
      monthlyStats: {},
      recentForm: [],
      winPercentage: '0.0',
      averagePosition: 0,
      careerSpan: {
        firstRace: null,
        lastRace: null,
        activeDays: 0
      }
    }
  }

  static updateCareerStats(summary, race, currentDay) {
    // Ensure summary has all required properties
    if (!summary.bestTimes) summary.bestTimes = {}
    if (!summary.circuitStats) summary.circuitStats = {}
    if (!summary.monthlyStats) summary.monthlyStats = {}
    if (!summary.careerSpan) summary.careerSpan = {}
    
    // Update basic stats
    if (race.position === 1) {
      summary.careerWins++
    }

    // Estimate earnings
    const earnings = this.estimateRaceEarnings(race.position, race.circuit)
    summary.careerEarnings += earnings

    // Update best times
    if (race.raceType && race.finishTime) {
      if (!summary.bestTimes[race.raceType] || race.finishTime < summary.bestTimes[race.raceType]) {
        summary.bestTimes[race.raceType] = race.finishTime
      }
    }

    // Update circuit stats
    const circuitId = race.circuit?.id || 'unknown'
    if (!summary.circuitStats[circuitId]) {
      summary.circuitStats[circuitId] = {
        races: 0,
        wins: 0,
        bestTime: Infinity
      }
    }
    summary.circuitStats[circuitId].races++
    if (race.position === 1) {
      summary.circuitStats[circuitId].wins++
    }
    if (race.finishTime && race.finishTime < summary.circuitStats[circuitId].bestTime) {
      summary.circuitStats[circuitId].bestTime = race.finishTime
    }

    // Update monthly stats
    const month = this.getMonthFromDay(race.day)
    if (!summary.monthlyStats[month]) {
      summary.monthlyStats[month] = {
        races: 0,
        wins: 0,
        earnings: 0
      }
    }
    summary.monthlyStats[month].races++
    if (race.position === 1) {
      summary.monthlyStats[month].wins++
    }
    summary.monthlyStats[month].earnings += earnings

    // Update career span
    if (!summary.careerSpan.firstRace || race.day < summary.careerSpan.firstRace) {
      summary.careerSpan.firstRace = race.day
    }
    if (!summary.careerSpan.lastRace || race.day > summary.careerSpan.lastRace) {
      summary.careerSpan.lastRace = race.day
    }
    summary.careerSpan.activeDays = summary.careerSpan.lastRace - summary.careerSpan.firstRace + 1
  }

  static estimateRaceEarnings(position, circuit) {
    const baseRewards = {
      1: 4000,
      2: 2000,
      3: 1000
    }
    
    if (position > 3) return 0
    
    const baseReward = baseRewards[position]
    const multiplier = circuit?.rewardMultiplier || 1
    
    return Math.floor(baseReward * multiplier)
  }

  static calculateRecentForm(raceHistory) {
    const recentRaces = raceHistory.slice(-5)
    return recentRaces.map(race => ({
      position: race.position,
      raceType: race.raceType,
      day: race.day
    }))
  }

  static calculateAveragePosition(raceHistory) {
    if (!raceHistory.length) return 0
    
    const total = raceHistory.reduce((sum, race) => sum + race.position, 0)
    return (total / raceHistory.length).toFixed(2)
  }

  static findPeakPerformance(raceHistory) {
    if (raceHistory.length < 5) return null

    let bestStreak = { wins: 0, start: 0, end: 0 }
    let currentStreak = { wins: 0, start: 0 }

    for (let i = 0; i < raceHistory.length; i++) {
      if (raceHistory[i].position <= 2) { // Top 2 finishes
        if (currentStreak.wins === 0) {
          currentStreak.start = i
        }
        currentStreak.wins++
      } else {
        if (currentStreak.wins > bestStreak.wins) {
          bestStreak = {
            wins: currentStreak.wins,
            start: currentStreak.start,
            end: i - 1
          }
        }
        currentStreak = { wins: 0, start: 0 }
      }
    }

    // Check final streak
    if (currentStreak.wins > bestStreak.wins) {
      bestStreak = {
        wins: currentStreak.wins,
        start: currentStreak.start,
        end: raceHistory.length - 1
      }
    }

    return bestStreak.wins > 2 ? bestStreak : null
  }

  static getMonthFromDay(day) {
    return Math.floor((day - 1) / 32) + 1
  }

  static archiveOldRaces(gameStore) {
    const currentDay = gameStore.currentDay
    const cutoffDay = currentDay - this.archiveThresholdDays
    let racesArchived = 0

    gameStore.chocobos.forEach(chocobo => {
      if (!chocobo.raceHistory) return

      const oldRaces = chocobo.raceHistory.filter(race => race.day < cutoffDay)
      const recentRaces = chocobo.raceHistory.filter(race => race.day >= cutoffDay)

      if (oldRaces.length > 0) {
        // Create or update career summary
        if (!chocobo.careerSummary) {
          chocobo.careerSummary = this.createChocoboCareerSummary(chocobo, currentDay)
        } else {
          // Update existing summary with old races
          oldRaces.forEach(race => {
            this.updateCareerStats(chocobo.careerSummary, race, currentDay)
          })
        }

        // Keep only recent races
        chocobo.raceHistory = recentRaces
        racesArchived += oldRaces.length
      }
    })

    // Races archived to career summaries

    return racesArchived
  }

  static generateGlobalLeaderboards(gameStore) {
    const leaderboards = {
      mostWins: [],
      bestTimes: {
        short: [],
        medium: [],
        long: []
      },
      highestEarnings: [],
      bestWinPercentage: [],
      generatedAt: gameStore.currentDay
    }

    const allStats = []

    // Collect stats from all chocobos
    gameStore.chocobos.forEach(chocobo => {
      let stats = chocobo.careerSummary
      
      if (!stats) {
        stats = this.createChocoboCareerSummary(chocobo, gameStore.currentDay)
      }

      if (stats.totalRaces > 0) {
        allStats.push(stats)
      }
    })

    // Generate leaderboards
    leaderboards.mostWins = allStats
      .sort((a, b) => b.careerWins - a.careerWins)
      .slice(0, 10)

    leaderboards.highestEarnings = allStats
      .sort((a, b) => b.careerEarnings - a.careerEarnings)
      .slice(0, 10)

    leaderboards.bestWinPercentage = allStats
      .filter(s => s.totalRaces >= 10) // Minimum 10 races
      .sort((a, b) => parseFloat(b.winPercentage) - parseFloat(a.winPercentage))
      .slice(0, 10)

    // Best times by race type
    Object.keys(leaderboards.bestTimes).forEach(raceType => {
      leaderboards.bestTimes[raceType] = allStats
        .filter(s => s.bestTimes[raceType])
        .sort((a, b) => a.bestTimes[raceType] - b.bestTimes[raceType])
        .slice(0, 10)
    })

    return leaderboards
  }

  static getHistoricalTrends(gameStore, monthsBack = 12) {
    const currentMonth = this.getMonthFromDay(gameStore.currentDay)
    const trends = {
      totalRaces: {},
      averageParticipation: {},
      winDistribution: {},
      circuitPopularity: {}
    }

    for (let i = 0; i < monthsBack; i++) {
      const month = currentMonth - i
      if (month <= 0) break

      trends.totalRaces[month] = 0
      trends.averageParticipation[month] = 0
      trends.winDistribution[month] = {}
      trends.circuitPopularity[month] = {}
    }

    // Analyze historical data
    gameStore.chocobos.forEach(chocobo => {
      if (!chocobo.raceHistory) return

      chocobo.raceHistory.forEach(race => {
        const raceMonth = this.getMonthFromDay(race.day)
        
        if (trends.totalRaces[raceMonth] !== undefined) {
          trends.totalRaces[raceMonth]++
          
          // Track circuit popularity
          const circuitId = race.circuit?.id || 'unknown'
          if (!trends.circuitPopularity[raceMonth][circuitId]) {
            trends.circuitPopularity[raceMonth][circuitId] = 0
          }
          trends.circuitPopularity[raceMonth][circuitId]++

          // Track wins by color
          if (race.position === 1) {
            const color = chocobo.color
            if (!trends.winDistribution[raceMonth][color]) {
              trends.winDistribution[raceMonth][color] = 0
            }
            trends.winDistribution[raceMonth][color]++
          }
        }
      })
    })

    return trends
  }

  static cleanupHistoricalData(gameStore) {
    let cleanedData = 0

    // Remove very old career summaries for retired chocobos
    const activeChocoboIds = new Set(gameStore.chocobos.map(c => c.id))
    
    if (gameStore.retirementHistory) {
      gameStore.retirementHistory.forEach(retirement => {
        if (!activeChocoboIds.has(retirement.chocoboId)) {
          // Keep basic retirement info but remove detailed career data
          delete retirement.detailedStats
          cleanedData++
        }
      })
    }

    // Archive old races
    cleanedData += this.archiveOldRaces(gameStore)

    // Historical data cleanup completed
    return cleanedData
  }
}